import { useCallback, useEffect, useState } from "react";

function getLandingNavigation() {
  return typeof performance === "undefined" ? null : performance.getEntriesByType("navigation")[0];
}

function getInitialDocumentPath(navigation) {
  if (typeof window === "undefined") {
    return "";
  }
  return navigation?.name ? new URL(navigation.name, window.location.origin).pathname : "";
}

function getLandingIntroDocumentKey(navigation, initialDocumentPath) {
  return `${initialDocumentPath}:${performance.timeOrigin || navigation?.name || ""}`;
}

function shouldShowLandingIntro() {
  if (typeof window === "undefined") {
    return false;
  }

  const navigation = getLandingNavigation();
  const initialDocumentPath = getInitialDocumentPath(navigation);
  const isInitialHomepageDocument = /^\/(?:l\/[^/]+)?\/?$/.test(initialDocumentPath);
  if (!isInitialHomepageDocument) {
    return false;
  }

  try {
    const documentIntroKey = getLandingIntroDocumentKey(navigation, initialDocumentPath);
    if (navigation?.type === "reload") {
      return window.sessionStorage.getItem("mincool.landingIntroDocument") !== documentIntroKey;
    }

    const hasSeenIntro = window.sessionStorage.getItem("mincool.landingIntroSeen") === "1";
    return !hasSeenIntro;
  } catch {
    return true;
  }
}

export function useLandingIntro() {
  const [showIntro] = useState(shouldShowLandingIntro);
  const [introPending, setIntroPending] = useState(showIntro);
  const handleIntroExit = useCallback(() => setIntroPending(false), []);

  useEffect(() => {
    if (!showIntro) {
      return;
    }

    try {
      const navigation = getLandingNavigation();
      const initialDocumentPath = getInitialDocumentPath(navigation);
      window.sessionStorage.setItem("mincool.landingIntroSeen", "1");
      window.sessionStorage.setItem(
        "mincool.landingIntroDocument",
        getLandingIntroDocumentKey(navigation, initialDocumentPath),
      );
    } catch {
      // The branded intro can still run when browser storage is unavailable.
    }
  }, [showIntro]);

  return { showIntro, introPending, handleIntroExit };
}
